import type { LayerConfig } from '../api/types';

export interface PopupContent {
  heading: string;
  rows: { field: string; value: string }[];
}

export function formatValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '–';
  if (typeof value === 'number')
    return Number.isInteger(value) ? String(value) : value.toLocaleString(undefined, { maximumFractionDigits: 3 });
  if (typeof value === 'string') return value;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return JSON.stringify(value);
}

/**
 * Heading: `popup.title` names a field (as packs do); when the feature has that field its value is the heading,
 * otherwise the text itself is used. Fallback: the layer title.
 */
export function buildPopup(
  layer: Pick<LayerConfig, 'title' | 'popup'>,
  props: Readonly<Record<string, unknown>>,
): PopupContent {
  const titleKey = layer.popup?.title;
  const fromField = titleKey !== undefined && titleKey in props ? formatValue(props[titleKey]) : undefined;
  const heading = fromField ?? titleKey ?? layer.title;
  const rows = (layer.popup?.fields ?? []).map((field) => ({ field, value: formatValue(props[field]) }));
  return { heading, rows };
}

export interface Placement {
  left: number;
  top: number;
  /** true when the popup sits below the point because there is no room above */
  below: boolean;
}

/**
 * Where to put a popup of `size` for a click at `point`, inside a container of `bounds` (all in container px).
 * Prefers above the point; flips below when the top would clip; clamps horizontally so it never leaves the map.
 */
export function placePopup(
  point: { x: number; y: number },
  size: { width: number; height: number },
  bounds: { width: number; height: number },
  gap = 12,
  margin = 8,
): Placement {
  const below = point.y - gap - size.height < margin;
  const top = below ? point.y + gap : point.y - gap - size.height;
  const maxLeft = Math.max(margin, bounds.width - size.width - margin);
  const left = Math.min(Math.max(point.x - size.width / 2, margin), maxLeft);
  return { left, top, below };
}
